import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { FirestoreService } from '../../services/firestore.service';
import { OrganizationData, VerificationChecks } from '../../shared/models/organization-data.model';
import { UserRole } from '../../shared/models/user.model';
import { VerificationChecksComponent } from './components/verification-checks/verification-checks.component';

@Component({
    imports: [
        CommonModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        VerificationChecksComponent,
    ],
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
    public displayedColumns: string[] = [
        'logo',
        'name',
        'primaryContactName',
        'phone',
        'email',
        'hostingUrl',
        'status',
        'verification',
        'actions',
    ];
    public dataSource = new MatTableDataSource<OrganizationData>([]);
    public canManageOrganizations$!: Observable<boolean>;
    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private readonly firestoreService: FirestoreService,
        private readonly authService: AuthService,
        private readonly snackBar: MatSnackBar,
        private readonly router: Router
    ) {
        this.initObservables();
    }

    private initObservables(): void {
        this.canManageOrganizations$ = this.authService
            .getAuthenticatedUserRole()
            .pipe(map((role) => role === UserRole.SUPER_ADMIN || role === UserRole.ADMIN));
    }

    public ngOnInit(): void {
        this.loadOrganizations();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private loadOrganizations(): void {
        this.firestoreService
            .getOrganizations()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((organizations) => {
                this.mapAndUpdateFirebase(organizations);

                const organizationsWithHostingUrl: OrganizationData[] = organizations.map((organization) => ({
                    ...organization,
                    hostingUrl: this.normalizeHostingUrl(this.resolveHostingUrl(organization)),
                }));

                const sortedOrganizations: OrganizationData[] = [...organizationsWithHostingUrl].sort((a, b) => {
                    if (!a.createdAt && !b.createdAt) return 0;
                    if (!a.createdAt) return 1;
                    if (!b.createdAt) return -1;

                    return b.createdAt.getTime() - a.createdAt.getTime();
                });
                this.dataSource.data = sortedOrganizations;
            });
    }

    private resolveHostingUrl(organization: OrganizationData): string {
        const customDomainUrl: string =
            (organization.customDomain ?? '').trim() || (organization.domainValue ?? '').trim();

        if (customDomainUrl.length > 0) {
            return customDomainUrl;
        }

        const defaultAppUrl: string = (organization.appUrl ?? '').trim();
        return defaultAppUrl;
    }

    private normalizeHostingUrl(url: string): string {
        if (!url) {
            return '';
        }
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    }

    public onPressDelete(id: string, organizationName: string): void {
        if (confirm('Are you sure you want to delete this organization?')) {
            if (environment.production) {
                this.updateOrganizationStatus(id, organizationName, 'disabled');
            } else {
                this.deleteOrganization(id);
            }
        }
    }

    private deleteOrganization(id: string): void {
        this.firestoreService
            .deleteOrganization(id)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.snackBar.open('Organization deleted successfully', 'Close', {
                        duration: 3000,
                    });
                },
                error: () => {
                    this.snackBar.open('Error deleting organization', 'Close', {
                        duration: 3000,
                    });
                },
            });
    }

    private updateOrganizationStatus(id: string, organizationName: string, status: 'active' | 'disabled'): void {
        this.firestoreService
            .updateOrganization(id, { status })
            .pipe(take(1))
            .subscribe({
                next: () => {
                    // Previously we deleted Firebase Hosting entries here; that flow was removed.
                    this.snackBar.open(`Organization ${status} successfully`, 'Close', {
                        duration: 3000,
                    });
                },
                error: () => {
                    this.snackBar.open(`Error updating organization status to ${status}`, 'Close', { duration: 3000 });
                },
            });
    }

    public goToCreateApp(): void {
        this.router.navigate(['/generate-app']);
    }

    public goToEditApp(appId: string): void {
        this.router.navigate([`/app/${appId}/edit`]);
    }

    private mapAndUpdateFirebase(organization: OrganizationData[]): void {
        organization.forEach((org) => {
            if (org.mainLogoLightUrl) {
                return;
            }
            org.mainLogoLightUrl =
                org.logoLightUrl ??
                'https://firebasestorage.googleapis.com/v0/b/cargo-facilities-studio.firebasestorage.app/o/logos%2FCargoSprint-Horizontal.svg?alt=media&token=e15fa9e8-fe75-43a0-8eff-161d8f01015a';
            org.mainLogoDarkUrl =
                org.logoDarkUrl ??
                'https://firebasestorage.googleapis.com/v0/b/cargo-facilities-studio.firebasestorage.app/o/logos%2FCargoSprint-Horizontal.svg?alt=media&token=e15fa9e8-fe75-43a0-8eff-161d8f01015a';
            this.firestoreService.updateOrganization(org.id!, {
                mainLogoLightUrl: org.mainLogoLightUrl,
                mainLogoDarkUrl: org.mainLogoDarkUrl,
            });
        });
    }

    public onVerificationChange(orgId: string, updated: VerificationChecks): void {
        const index = this.dataSource.data.findIndex((o) => o.id === orgId);
        if (index !== -1) {
            this.dataSource.data[index] = { ...this.dataSource.data[index], verificationChecks: updated };
            this.dataSource.data = [...this.dataSource.data]; // trigger change detection
        }
    }
}
